import type { Queryable } from '@mode/shared/util-js'; // eslint-disable-line
import { getRlsIsUnlinkable } from '@mode/shared/util-js';

type Query =
  | {
      id: string;
      view: any;
    }
  | string
  | any;

interface PostMessageable {
  postMessage: Window['postMessage'];
}

/**
 * @constant
 * @type {string}
 */
const DEVELOPMENT = 'development';

/**
 * @description Report paths that are considered unlinkable. When a report is
 *  unlinkable, there is no parent-frame app that can receive routing actions
 *  and the embedded app should fall back to native link (anchor) behavior.
 *  There are only 2 files (see link below) right now that fall into the unlinkable
 *  category, and they will have a meta tag identifying them as The Unlinkables
 *  See: https://github.com/mode/webapp/blob/r823.0.0/app/helpers/embeds_helper.rb#L21
 * @param window
 */
export function getReportIsLinkable(window: Queryable): boolean {
  return getRlsIsUnlinkable(window) !== 'true';
}

/**
 * @description Development only console error when linking in an unlinkable scenario.
 *  Throws and catches an error with additional data so one can inspect the stack trace
 *  without having to do a bunch of console-logging or breakpoints
 */
export function __dev_traceIfLinkingTheUnlinkable(isLinkable: boolean, query: Query, type: string) {
  if (!isLinkable) {
    try {
      throw new Error(`@called ReportLinkService.linkToPage() in an unlinkable scenario`);
    } catch (error) {
      console.error(error, {
        query,
        type,
      });
    }
  }
}

/**
 * Posts a message to the parent window to html5 nav the app with uiRouter; here:
 * https://github.com/mode/webapp/blob/r821.0.0/frontend/src/components/embed-frame/report-viewer.component.ts#L366
 */
export function linkToPagePostMessage(targetWindow: PostMessageable, query: Query, type: string) {
  targetWindow.postMessage(
    {
      name: 'link',
      query,
      type,
    },
    '*'
  );
}

export default class ReportLinkService {
  static $inject = ['$window'];

  private reportIsLinkable: boolean;

  constructor(private $window: angular.IWindowService) {
    this.reportIsLinkable = getReportIsLinkable($window.document);
  }

  linkToPage(query: Query, type: string) {
    linkToPagePostMessage(this.$window.parent, query, type);

    if (process.env.NODE_ENV === DEVELOPMENT) {
      __dev_traceIfLinkingTheUnlinkable(this.isLinkable(), query, type);
    }
  }

  isLinkable() {
    return this.reportIsLinkable;
  }
}
